import React, { useState, useEffect, useRef } from 'react';
import { Shield, Eye, Check, AlertTriangle, RefreshCw, XCircle, Image as ImageIcon } from 'lucide-react';
import { fetchModerationMedia, processMediaAction } from '../utils/api';

export interface ModerationItem {
  id: string;
  userId: string;
  userName: string;
  age: number;
  reportCount: number;
  reason: string;
  imageUrl: string;
  status: 'pending' | 'approved' | 'rejected' | 'flagged';
}

export const ModerationQueue: React.FC = () => {
  const [items, setItems] = useState<ModerationItem[]>([]);
  const [revealedImages, setRevealedImages] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [dataMode, setDataMode] = useState<'test' | 'live'>('live');
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');

  const canvasRefs = useRef<Record<string, HTMLCanvasElement | null>>({});

  const loadMedia = async () => {
    setLoading(true);
    try {
      const data = await fetchModerationMedia();
      if (data && data.media) {
        setItems(data.media);
      }
      if (data && data.mode) {
        setDataMode(data.mode);
      }
    } catch (e) {
      console.error('Failed to load moderation media:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMedia();
    const handleModeChanged = () => {
      loadMedia();
    };
    window.addEventListener('system-mode-changed', handleModeChanged);
    return () => window.removeEventListener('system-mode-changed', handleModeChanged);
  }, []);

  // Draw blurred canvas to safeguard moderators from NSFW / flagged content
  const drawBlurredImage = (id: string, imageUrl: string) => {
    const canvas = canvasRefs.current[id];
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;

    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      ctx.save();
      ctx.filter = 'blur(16px)';
      ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height);
      ctx.restore();

      ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.font = 'bold 13px Outfit, Inter, sans-serif';
      ctx.fillStyle = '#FFFFFF';
      ctx.textAlign = 'center';
      ctx.fillText('Protected Media Preview', canvas.width / 2, canvas.height / 2 - 8);

      ctx.font = '11px Outfit, Inter, sans-serif';
      ctx.fillStyle = '#ef4444';
      ctx.fillText('Click to unblur / examine', canvas.width / 2, canvas.height / 2 + 14);
    };
  };

  useEffect(() => {
    items.forEach(item => {
      if (!revealedImages[item.id]) {
        drawBlurredImage(item.id, item.imageUrl);
      }
    });
  }, [items, revealedImages]);

  const toggleReveal = (id: string) => {
    setRevealedImages(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleAction = async (itemId: string, action: 'approve' | 'reject') => {
    setActionInProgress(itemId);
    try {
      await processMediaAction(itemId, action);
      setItems(prev => prev.map(m => {
        if (m.id !== itemId) return m;
        return {
          ...m,
          status: action === 'approve' ? 'approved' : 'rejected',
        };
      }));
    } catch (e) {
      console.error('Failed to process moderation action:', e);
    } finally {
      setActionInProgress(null);
    }
  };

  const filteredItems = items.filter(i => {
    if (filter === 'all') return true;
    return i.status === filter;
  });

  const pendingCount = items.filter(i => i.status === 'pending').length;

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h1 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight text-white flex items-center gap-2.5">
              <Shield className="text-primaryRed w-7 h-7" />
              Content Moderation Queue
            </h1>
            {dataMode === 'test' ? (
              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30 flex items-center gap-1.5 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                🧪 Test Sandbox Media
              </span>
            ) : (
              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1.5 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                ⚡ Live R2/D1 Media
              </span>
            )}
            {pendingCount > 0 && (
              <span className="badge badge-red text-xs">{pendingCount} Pending</span>
            )}
          </div>
          <p className="text-sm text-zinc-400">
            {dataMode === 'test' ? 'Simulated image verification queue for QA preview' : 'Zero-Trust sensitive media scanner • Automated blur shielding • Cloudflare R2 images'}
          </p>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap">
          <div className="flex rounded-xl bg-zinc-900 border border-darkBorder p-1 text-xs font-semibold">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 rounded-lg transition ${filter === 'all' ? 'bg-primaryRed text-white' : 'text-zinc-400 hover:text-white'}`}
            >
              All ({items.length})
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`px-3 py-1.5 rounded-lg transition ${filter === 'pending' ? 'bg-primaryRed text-white' : 'text-zinc-400 hover:text-white'}`}
            >
              Pending ({pendingCount})
            </button>
            <button
              onClick={() => setFilter('approved')}
              className={`px-3 py-1.5 rounded-lg transition ${filter === 'approved' ? 'bg-primaryRed text-white' : 'text-zinc-400 hover:text-white'}`}
            >
              Approved
            </button>
          </div>

          <button
            onClick={loadMedia}
            disabled={loading}
            className="btn-ghost flex items-center gap-2 text-sm"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Grid of Moderation Cards */}
      {loading && items.length === 0 ? (
        <div className="glass rounded-2xl p-16 text-center text-zinc-400 border border-darkBorder">
          <RefreshCw className="animate-spin w-8 h-8 mx-auto mb-3 text-primaryRed" />
          <p className="font-semibold text-white">Loading pending media queue from Edge D1 & R2...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="glass rounded-2xl p-16 text-center text-zinc-400 border border-darkBorder">
          <ImageIcon className="w-12 h-12 mx-auto mb-3 text-zinc-600" />
          <p className="text-base font-semibold text-zinc-200">No media items in this queue</p>
          <p className="text-xs text-zinc-500 mt-1">All incoming photos and user uploads have been reviewed.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map(item => (
            <div
              key={item.id}
              className="glass rounded-2xl overflow-hidden border border-darkBorder flex flex-col hover:border-zinc-700 transition duration-200 shadow-xl"
            >
              {/* Media Preview Box with Sensitive Blur */}
              <div className="relative h-64 bg-zinc-950 flex items-center justify-center overflow-hidden">
                {!revealedImages[item.id] ? (
                  <canvas
                    ref={el => { canvasRefs.current[item.id] = el; }}
                    width={380}
                    height={256}
                    onClick={() => toggleReveal(item.id)}
                    className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition"
                  />
                ) : (
                  <img
                    src={item.imageUrl}
                    alt={item.userName}
                    className="w-full h-full object-cover"
                  />
                )}

                {/* Status Badge */}
                <div className="absolute top-3 right-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold tracking-wider uppercase backdrop-blur-md ${
                    item.status === 'pending' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                    item.status === 'approved' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                    'bg-red-500/20 text-red-400 border border-red-500/30'
                  }`}>
                    {item.status}
                  </span>
                </div>

                {/* Reveal Toggle */}
                <button
                  onClick={() => toggleReveal(item.id)}
                  className="absolute bottom-3 left-3 bg-zinc-950/80 hover:bg-black border border-zinc-700/60 px-2.5 py-1.5 rounded-lg text-white text-xs flex items-center gap-1.5 backdrop-blur-md transition shadow-md"
                >
                  <Eye className="w-3.5 h-3.5" />
                  {revealedImages[item.id] ? 'Re-blur Preview' : 'Reveal Photo'}
                </button>
              </div>

              {/* Card Body */}
              <div className="p-5 flex-1 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-bold text-base text-white">
                      {item.userName}, {item.age}
                    </h3>
                    {item.reportCount > 0 ? (
                      <span className="badge badge-red text-xs flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {item.reportCount} flags
                      </span>
                    ) : (
                      <span className="text-xs text-zinc-500 font-medium">New Upload</span>
                    )}
                  </div>
                  <p className="text-xs text-zinc-500">User ID: {item.userId} • Ref: {item.id}</p>

                  <div className="mt-4 bg-zinc-900/60 border border-darkBorder rounded-xl p-3">
                    <p className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider">
                      Audit Reason
                    </p>
                    <p className="text-xs text-zinc-200 mt-1 font-medium">{item.reason}</p>
                  </div>
                </div>

                {/* Action Controls */}
                {item.status === 'pending' ? (
                  <div className="flex items-center gap-2.5 mt-5 pt-4 border-t border-darkBorder">
                    <button
                      onClick={() => handleAction(item.id, 'approve')}
                      disabled={actionInProgress === item.id}
                      className="flex-1 flex justify-center items-center gap-1.5 bg-green-500/10 hover:bg-green-500 border border-green-500/30 hover:border-green-500 text-green-400 hover:text-white py-2.5 rounded-xl text-xs font-bold transition shadow-sm"
                    >
                      <Check className="w-4 h-4" />
                      Approve Photo
                    </button>
                    <button
                      onClick={() => handleAction(item.id, 'reject')}
                      disabled={actionInProgress === item.id}
                      className="flex-1 flex justify-center items-center gap-1.5 bg-red-500/10 hover:bg-red-500 border border-red-500/30 hover:border-red-500 text-red-400 hover:text-white py-2.5 rounded-xl text-xs font-bold transition shadow-sm"
                    >
                      <XCircle className="w-4 h-4" />
                      Reject & Remove
                    </button>
                  </div>
                ) : (
                  <div className="mt-5 pt-3 border-t border-darkBorder text-center">
                    <span className="text-xs text-zinc-500">
                      Processed by admin as <strong className="text-zinc-300 capitalize">{item.status}</strong>
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
